import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	useDownloadVanCreditDocumentsLazyQuery,
	DocumentFormat
} from '@/graphql/__generated__/van-credits';
import { Download } from 'react-feather';
import Button from '@/UIlibrary/Button/Button';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';

interface CreditDocumentsProps {
	clientId: string;
	creditId: string;
	refetchTrigger?: number;
}

interface DocumentState {
	base64: string | null;
	loading: boolean;
	error: string | null;
}

const CreditDocuments: React.FC<CreditDocumentsProps> = ({
	clientId,
	creditId,
	refetchTrigger
}) => {
	const t = useTranslations('CreditDocuments');
	const vanCreditsClient = useApolloClient(SCHEMAS.VAN_CREDITS);

	const [contractState, setContractState] = useState<DocumentState>({
		base64: null,
		loading: true,
		error: null
	});

	const [promissoryNoteState, setPromissoryNoteState] = useState<DocumentState>({
		base64: null,
		loading: true,
		error: null
	});

	const [fetchContract] = useDownloadVanCreditDocumentsLazyQuery({
		client: vanCreditsClient,
		onCompleted: (data) => {
			if (data.downloadDocuments?.pdfBase64) {
				setContractState({
					base64: data.downloadDocuments.pdfBase64,
					loading: false,
					error: null
				});
			} else {
				setContractState({
					base64: null,
					loading: false,
					error: 'No document data received'
				});
			}
		},
		onError: (error) => {
			setContractState({
				base64: null,
				loading: false,
				error: error.message
			});
		}
	});

	const [fetchPromissoryNote] = useDownloadVanCreditDocumentsLazyQuery({
		client: vanCreditsClient,
		onCompleted: (data) => {
			if (data.downloadDocuments?.pdfBase64) {
				setPromissoryNoteState({
					base64: data.downloadDocuments.pdfBase64,
					loading: false,
					error: null
				});
			} else {
				setPromissoryNoteState({
					base64: null,
					loading: false,
					error: 'No document data received'
				});
			}
		},
		onError: (error) => {
			setPromissoryNoteState({
				base64: null,
				loading: false,
				error: error.message
			});
		}
	});

	useEffect(() => {
		const fetchDocumentsSequentially = async () => {
			setContractState({ base64: null, loading: true, error: null });
			setPromissoryNoteState({ base64: null, loading: true, error: null });

			await fetchContract({
				variables: {
					clientId,
					creditId,
					documentFormat: DocumentFormat.CONTRACT
				}
			});

			await fetchPromissoryNote({
				variables: {
					clientId,
					creditId,
					documentFormat: DocumentFormat.PROMISSORY_NOTE
				}
			});
		};

		fetchDocumentsSequentially();
	}, [clientId, creditId, refetchTrigger, fetchContract, fetchPromissoryNote]);

	const base64toBlob = (base64: string, contentType: string): Blob => {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: contentType });
	};

	const downloadPdf = (base64: string, filename: string) => {
		const blob = base64toBlob(base64, 'application/pdf');
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const handleDownloadContract = () => {
		if (contractState.base64) {
			downloadPdf(contractState.base64, `contrato-${creditId}.pdf`);
		}
	};

	const handleDownloadPromissoryNote = () => {
		if (promissoryNoteState.base64) {
			downloadPdf(promissoryNoteState.base64, `pagare-${creditId}.pdf`);
		}
	};

	const renderDocumentSection = (title: string, state: DocumentState, onDownload: () => void) => {
		return (
			<div className="flex flex-col">
				<div className="flex items-center justify-between mb-4">
					<Typography weight="semibold" size="xl">
						{title}
					</Typography>
					{state.base64 && !state.loading && (
						<Button
							onClick={onDownload}
							label={t('Download')}
							color="primary"
							size="small"
							startIcon={<Download size={16} />}
						/>
					)}
				</div>

				{state.loading && (
					<div className="flex items-center justify-center h-[75vh] border border-gray-300 rounded">
						<div className="flex flex-col items-center">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
							<p className="mt-4 text-gray-600">{t('LoadingDocument')}</p>
						</div>
					</div>
				)}

				{state.error && !state.loading && (
					<div className="flex items-center justify-center h-[75vh] border border-red-300 bg-red-50 rounded">
						<div className="text-center px-4">
							<p className="text-red-600 font-semibold">{t('ErrorLoadingDocument')}</p>
							<p className="text-sm text-red-500 mt-2">{state.error}</p>
						</div>
					</div>
				)}

				{state.base64 && !state.loading && !state.error && (
					<iframe
						src={`data:application/pdf;base64,${state.base64}`}
						className="w-full h-[50vh] border border-gray-300 rounded"
						title={title}
					/>
				)}
			</div>
		);
	};

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5"
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<div className="mb-2">
				<Typography weight="semibold" size="lg">
					{t('CreditDocuments')}
				</Typography>
			</div>
			<div className="grid grid-cols-1 gap-6">
				{renderDocumentSection(t('Contract'), contractState, handleDownloadContract)}
				{renderDocumentSection(
					t('PromissoryNote'),
					promissoryNoteState,
					handleDownloadPromissoryNote
				)}
			</div>
		</div>
	);
};

export default CreditDocuments;
